import React, { createContext, useContext, useState, ReactNode } from "react";
import { SimpleOrder } from "../interfaces/simpleOrder";
import { Order } from "../interfaces/order";
interface PendingOrdersContextType {
  pendingOrders: SimpleOrder[];
  setPendingOrders: React.Dispatch<React.SetStateAction<SimpleOrder[]>>;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  searchApplied: string;
  setSearchApplied: React.Dispatch<React.SetStateAction<string>>;
  startDate: string | null;
  setStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  endDate: string | null;
  setEndDate: React.Dispatch<React.SetStateAction<string | null>>;
  orderType: string | null;
  setOrderType: React.Dispatch<React.SetStateAction<string | null>>;
  searchType: string | null;
  setSearchType: React.Dispatch<React.SetStateAction<string | null>>;
  appliedStartDate: string | null;
  setAppliedStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  appliedEndDate: string | null;
  setAppliedEndDate: React.Dispatch<React.SetStateAction<string | null>>;
  appliedOrderType: string | null;
  setAppliedOrderType: React.Dispatch<React.SetStateAction<string | null>>;
  appliedSearchType: string | null;
  setAppliedSearchType: React.Dispatch<React.SetStateAction<string | null>>;
  hasMore: boolean;
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
  updateOrderInPendingOrdersList: (
    updatedOrder: Order,
    statusID: number,
  ) => void;
}

const PendingOrdersContext = createContext<
  PendingOrdersContextType | undefined
>(undefined);

export const PendingOrdersProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [pendingOrders, setPendingOrders] = useState<SimpleOrder[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [orderType, setOrderType] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<string | null>("Candidate");
  const [appliedStartDate, setAppliedStartDate] = useState<string | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<string | null>(null);
  const [appliedOrderType, setAppliedOrderType] = useState<string | null>(null);
  const [appliedSearchType, setAppliedSearchType] = useState<string | null>(
    "Candidate",
  );
  const [hasMore, setHasMore] = useState(true);

  const updateOrderInPendingOrdersList = (order: Order, statusID: number) => {
    if (statusID !== 1) {
      setPendingOrders((prev) =>
        prev.filter((o) => o.orderID !== order.orderID),
      );
    } else {
      setPendingOrders((prev) =>
        prev.map((o) =>
          o.orderID === order.orderID
            ? {
                ...o,
                candidateFirstName: order.candidateFirstName,
                candidateLastName: order.candidateLastName,
                candidateMI: order.candidateMI,
                designation: order.designation ?? undefined,
                orderStatusID: statusID,
                pendingDate: new Date(),
              }
            : o,
        ),
      );
    }
  };

  return (
    <PendingOrdersContext.Provider
      value={{
        pendingOrders,
        setPendingOrders,
        pageNumber,
        setPageNumber,
        searchTerm,
        setSearchTerm,
        searchApplied,
        setSearchApplied,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        orderType,
        setOrderType,
        searchType,
        setSearchType,
        appliedStartDate,
        setAppliedStartDate,
        appliedEndDate,
        setAppliedEndDate,
        appliedOrderType,
        setAppliedOrderType,
        appliedSearchType,
        setAppliedSearchType,
        hasMore,
        setHasMore,
        updateOrderInPendingOrdersList,
      }}
    >
      {children}
    </PendingOrdersContext.Provider>
  );
};

export const usePendingOrdersContext = () => {
  const context = useContext(PendingOrdersContext);
  if (!context)
    throw new Error(
      "usePendingOrdersContext must be used within OrderProvider",
    );
  return context;
};
